import styled, { css, keyframes } from 'styled-components';
import { Stack, Button, Text } from '@solvhealth/jigsaw';
import React, { ReactNode } from 'react';
import BorderBoxReset from '~/components/util/BorderBoxReset';
import DefaultCardComponents, { CardProps } from './CardComponents';

/**
 * This file contains the H2 2022 Booking Flow components used in the AddInsurance step, overriding the
 * previous components in the BookingFlows insurance step. Those older components are now located in
 * ./CardComponents.tsx.
 */

// Static dimensions of insurance cards
export const CARD_WIDTH = 366;
const CARD_HEIGHT = 195;
// Static width of the "scan head" vertical bar that animates to give the appearance
// of scanning text off the card. Used to correctly offset the head's position during
// the animation. The pre-h2 2022 version has a bug where the head obscurs the card
// before the animation plays
const SCAN_HEAD_WIDTH = 5;

const CardStack = styled(Stack)`
  box-sizing: border-box;
  width: ${CARD_WIDTH}px;

  :not(:last-child) {
    margin-bottom: 24px;
  }
`;

const Card = (props: { children?: ReactNode } & CardProps) => {
  return (
    <CardStack
      alignItems="center"
      borderColor={props.sc.borderColor as any}
      borderRadius="base"
      // This version of the Card specificaly ignores the passed in value for borderStyle
      borderStyle={props.state === 'photo' ? 'unset' : 'dashed'}
      borderWidth="thin"
      minWidth={`${CARD_WIDTH}px`}
      position="relative"
      px={props.state === 'photo' ? 0 : 1}
      py={props.state === 'photo' ? 0 : 4}
      space={3}
    >
      {props.children}
    </CardStack>
  );
};

const CardContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CardHeader = (props: any) => {
  return <Text variant="h4">{props.children}</Text>;
};

const CardBody = (props: any) => {
  return (
    <Text color="gray-500" variant="body">
      {props.children}
    </Text>
  );
};

const UploadButton = ({ children, ...rest }: Parameters<typeof Button>[0]) => {
  return (
    <Button size="small" width="fit-content" {...rest}>
      {children}
    </Button>
  );
};

const Input = styled(DefaultCardComponents.Input)`
  width: ${CARD_WIDTH}px;
  border-width: 2px;
  border-color: black;
  border-style: solid;
  border-radius: 0.444rem; // "base" border-radius size from the style library
`;

const CardPicture = styled(DefaultCardComponents.CardPicture)`
  width: ${CARD_WIDTH}px;
  border-radius: 0.444rem; // "base" border-radius size from the style library
`;

const scanKeyframe = keyframes`
  from { transform: translateX(-${CARD_WIDTH + SCAN_HEAD_WIDTH}px); }
  to { transform: translateX(0); }
`;

const scanAnimation = css`
  animation: ${scanKeyframe} 2s linear infinite alternate;
`;

const ScanOverlay = styled(DefaultCardComponents.ScanOverlay)`
  width: ${CARD_WIDTH}px;
  transform: translateX(-${CARD_WIDTH + SCAN_HEAD_WIDTH}px);
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

const ScanHead = styled(DefaultCardComponents.ScanHead)`
  height: ${CARD_HEIGHT}px;
  top: 0px;
  transform: translateX(-${CARD_WIDTH + SCAN_HEAD_WIDTH}px);
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

export default {
  Card,
  CardPicture,
  ScanOverlay,
  ScanHead,
  CardHeader,
  CardBody,
  HintContainer: DefaultCardComponents.HintContainer,
  CardContainer,
  CardPlaceholder: DefaultCardComponents.CardPlaceholder,
  UploadButton,
  Input,
};
